import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import { Chart, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { AutoComplete } from 'primereact/autocomplete';
import npyjs from 'npyjs';
import './App.css';

// Registrando componentes do Chart.js
Chart.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const App: React.FC = () => {
  const [datasets, setDatasets] = useState<Array<any>>([]);
  const [highlightedDataset, setHighlightedDataset] = useState<number | null>(null);
  const [hiddenDatasets, setHiddenDatasets] = useState<number[]>([]);
  type Selection = {
    selectedDataset: any | null;
    filteredDataset: any[] | null;
    selectedData: any[]; // agora é array
    filteredData: any[] | null;
    numpyData: number[][];
    loading: boolean;
  };

  const [selections, setSelections] = useState<Selection[]>([
    {
      selectedDataset: null,
      filteredDataset: null,
      selectedData: [],
      filteredData: null,
      numpyData: [],
      loading: false,
    },
  ]);
  const [leftWidth, setLeftWidth] = useState(40); // porcentagem
  const [resizing, setResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [graphWidth, setGraphWidth] = useState(600);
  const [graphHeight, setGraphHeight] = useState(400);
  const [resizingGraph, setResizingGraph] = useState(false);
  const graphRef = useRef<HTMLDivElement>(null);
  const [filterSpectraText, setFilterSpectraText] = useState('');

  const searchDataset = (event: any, idx: number) => {
    setTimeout(() => {
      let _filtered = !event.query.trim().length
        ? [...datasets]
        : datasets.filter((dataset) =>
            dataset.name.toLowerCase().includes(event.query.toLowerCase())
          );
      setSelections((prev) =>
        prev.map((sel, i) =>
          i === idx ? { ...sel, filteredDataset: _filtered } : sel
        )
      );
    }, 250);
  };

  const searchData = (event: any, idx: number) => {
    setTimeout(() => {
      const datas = selections[idx]?.filteredData ?? [];
      let _filtered = !event.query.trim().length
        ? [...datas]
        : datas.filter((dataset) =>
            dataset.name.toLowerCase().includes(event.query.toLowerCase())
          );
      setSelections((prev) =>
        prev.map((sel, i) =>
          i === idx ? { ...sel, filteredData: _filtered } : sel
        )
      );
    }, 250);
  };

  useEffect(() => {
    // Fetch data from the API
    const fetchData = async () => {
      try {
        console.log('fetching')
        const response = await axios.get('https://api.plantspec.damine.com.br/DatasetSimple/');
        // Assuming the response contains an array of objects with 'name' and 'values' fields
        console.log('datasets', await response.data)
        const data = response.data.map((item: any) => ({
          name: item.name, // ou outro campo que desejar exibir
          value: item // Replace with actual path to the numpy array in your data
        }));
        setDatasets(data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    // Descobre qual seleção mudou de dataset
    const lastIdx = selections.findIndex(
      (sel, idx) =>
        sel.selectedDataset &&
        (!sel.filteredData || sel.loading)
    );
    if (lastIdx === -1) return;

    const sel = selections[lastIdx];
    if (!sel.selectedDataset) return;

    // Limpa espectros e ativa loading ANTES de buscar
    setSelections(prev =>
      prev.map((s, i) =>
        i === lastIdx
          ? {
              ...s,
              filteredData: null,
              selectedData: [],
              numpyData: [],
              loading: true,
            }
          : s
      )
    );

    axios
      .get(
        'https://api.plantspec.damine.com.br/DataAll/?dataset=' +
          sel.selectedDataset.value.id
      )
      .then((response) => {
        const datas = response.data.map((item: any) => ({
          name:
            'Clone ' +
            item.class_data +
            ' Cepa ' +
            item.sub_Cat2 +
            ' Folha ' +
            item.sub_Cat3 +
            ' Espectro ' +
            item.sub_Cat4,
          value: item,
        }));
        setSelections(prev =>
          prev.map((s, i) =>
            i === lastIdx
              ? {
                  ...s,
                  filteredData: datas,
                  loading: false,
                }
              : s
          )
        );
      });
    // eslint-disable-next-line
  }, [selections.map((s) => s.selectedDataset && s.selectedDataset.value?.id).join()]);

  useEffect(() => {
    // Quando selectedData muda, buscar numpy para cada espectro selecionado
    selections.forEach((sel, idx) => {
      if (sel.selectedData && sel.selectedData.length > 0) {
        setSelections((prev) =>
          prev.map((s, i) => (i === idx ? { ...s, loading: true } : s))
        );
        Promise.all(
          sel.selectedData.map((dataItem: any) =>
            axios
              .get(dataItem.value.data, { responseType: 'arraybuffer' })
              .then(async (response) => {
                const npyLoader = new npyjs();
                const numpyArray = await npyLoader.load(response.data);
                const n: number[] = [];
                for (let i = 6064; i < numpyArray.data.length; i++)
                  n.push(Number(numpyArray.data[i]));
                return n;
              })
              .catch(() => [])
          )
        ).then((allNumpyData) => {
          setSelections((prev) =>
            prev.map((s, i) =>
              i === idx ? { ...s, numpyData: allNumpyData, loading: false } : s
            )
          );
        });
      } else {
        // Se não há espectros selecionados, limpa numpyData
        setSelections((prev) =>
          prev.map((s, i) =>
            i === idx ? { ...s, numpyData: [], loading: false } : s
          )
        );
      }
    });
    // eslint-disable-next-line
  }, [selections.map((s) => (s.selectedData || []).map((d: any) => d?.value?.data).join(',')).join('|')]);

  // Função utilitária para gerar cores baseadas no grupo e espectro
  function getGroupColor(idx: number) {
    // Paleta de cores base para grupos (você pode ajustar as cores)
    const baseColors = [
      [52, 152, 219],   // azul
      [231, 76, 60],    // vermelho
      [46, 204, 113],   // verde
      [241, 196, 15],   // amarelo
      [155, 89, 182],   // roxo
      [230, 126, 34],   // laranja
      [26, 188, 156],   // turquesa
      [127, 140, 141],  // cinza
    ];
    return baseColors[idx % baseColors.length];
  }

  // Função para clarear/escurecer a cor para cada espectro
  function shadeColor(rgb: number[], percent: number) {
    // percent: -0.5 escurece, 0.5 clareia
    return rgb
      .map((c) =>
        Math.min(
          255,
          Math.max(0, Math.round(c + (percent * (percent > 0 ? (255 - c) : c))))
      )
    ).join(',');
  }

  // Monta datasets para o gráfico
  const chartData = {
    labels:
      selections
        .find((s) => s.numpyData && s.numpyData.length > 0 && s.numpyData[0].length > 0)
        ?.numpyData[0].map((_, i) => i) || [],
    datasets: selections.flatMap((sel, idx) => {
      const baseColor = getGroupColor(idx);
      const count = sel.numpyData?.length || 1;
      return (sel.numpyData || []).map((dataArr, j) => {
        // Variação de tom: do mais escuro ao mais claro no grupo
        const percent = count === 1 ? 0 : (j / (count - 1)) * 0.5 - 0.25; // -0.25 a +0.25
        const rgb = shadeColor(baseColor, percent);

        // Calcula o índice global do dataset
        const datasetIndex = selections
          .slice(0, idx)
          .reduce((acc, s) => acc + (s.numpyData?.length || 0), 0) + j;

        // Evidencia se for o selecionado
        const isHighlighted = highlightedDataset === null || highlightedDataset === datasetIndex;

        // Opacidade e espessura ajustadas conforme seleção
        const opacity = highlightedDataset === null
          ? 0.8 // nenhum selecionado: todos normais
          : isHighlighted
            ? 1   // selecionado: máximo destaque
            : 0.15; // não selecionado: bem esmaecido

        const borderWidth = isHighlighted ? 4 : 2; // selecionado mais espesso

        return {
          label:
            (sel.selectedDataset?.name || `Seleção ${idx + 1}`) +
            ' - ' +
            (sel.selectedData?.[j]?.name || `Espectro ${j + 1}`),
          data: dataArr,
          fill: false,
          backgroundColor: `rgba(${rgb},${opacity})`,
          borderColor: `rgba(${rgb},${opacity})`,
          borderWidth,
          pointRadius: isHighlighted ? 0 : 1,
          pointHoverRadius: 4,
          pointBackgroundColor: `rgba(${rgb},${opacity})`, // <-- opacidade dos pontos igual à linha
          pointBorderColor: `rgba(${rgb},${opacity})`,     // <-- opacidade dos pontos igual à linha
          tension: 0.2,
          _datasetIndex: datasetIndex, // <-- adicione isso
          hidden: hiddenDatasets.includes(datasetIndex),
        };
      });
    }),
  };

  // Custom plugin to draw the eye icon in the legend
  const legendEye = {
    id: 'legendEye',
    afterDraw: (chart: any) => {
      if (!chart.legend || !chart.legend.legendItems) return;
      const ctx = chart.ctx;
      chart.legend.legendItems.forEach((item: any, i: number) => {
        const box = chart.legend.legendHitBoxes[i];
        if (!box) return;
        // Eye icon position: à esquerda do box de cor
        const eyeGap = 10; // espaço entre olho e box de cor
        const x = box.left - eyeGap - 20; // 20 = raio horizontal do olho
        const y = box.top + box.height / 2;
        ctx.save();
        ctx.beginPath();
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#666';
        ctx.fillStyle = item.hidden ? '#ccc' : '#219150';
        // Draw eye outline
        ctx.ellipse(x, y, 10, 6, 0, 0, 2 * Math.PI);
        ctx.stroke();
        // Draw eye pupil if visible
        if (!item.hidden) {
          ctx.beginPath();
          ctx.arc(x, y, 2, 0, 2 * Math.PI);
          ctx.fill();
        }
        ctx.restore();
      });
    },
    afterEvent: (chart: any, evt: any) => {
      if (!chart.legend || !chart.legend.legendItems) return;
      if (evt.event.type !== 'click') return;
      const mouseX = evt.event.x;
      const mouseY = evt.event.y;
      chart.legend.legendItems.forEach((item: any, i: number) => {
        const box = chart.legend.legendHitBoxes[i];
        if (!box) return;
        // Eye icon area: à esquerda do box de cor
        const eyeGap = 10;
        const x = box.left - eyeGap - 20;
        const y = box.top + box.height / 2;
        const r = 10;
        if (
          mouseX >= x - r &&
          mouseX <= x + r &&
          mouseY >= y - r &&
          mouseY <= y + r
        ) {
          setHiddenDatasets((prev) =>
            prev.includes(item.datasetIndex)
              ? prev.filter((idx) => idx !== item.datasetIndex)
              : [...prev, item.datasetIndex]
          );
        }
      });
    },
  };

  // Configurações do gráfico
  const chartOptions = {
    plugins: {
      legend: {
        display: false, // <-- Oculta a legenda nativa
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const addSelection = () => {
    setSelections(prev => [
      ...prev,
      {
        selectedDataset: null,
        filteredDataset: null,
        selectedData: [],
        filteredData: null,
        numpyData: [],
        loading: false,
      },
    ]);
  };

  const startResizing = (e: React.MouseEvent) => {
    setResizing(true);
    e.preventDefault();
  };

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!resizing || !containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newLeftWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;
      if (newLeftWidth > 15 && newLeftWidth < 85) setLeftWidth(newLeftWidth);
    };
    const onMouseUp = () => setResizing(false);

    if (resizing) {
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [resizing]);

  useEffect(() => {
    function onMouseMove(e: MouseEvent) {
      if (!resizingGraph || !graphRef.current) return;
      const rect = graphRef.current.getBoundingClientRect();
      setGraphWidth(Math.max(300, e.clientX - rect.left));
      setGraphHeight(Math.max(200, e.clientY - rect.top));
    }
    function onMouseUp() {
      setResizingGraph(false);
    }
    if (resizingGraph) {
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [resizingGraph]);

  // Cálculo dos índices dos datasets para uso no gráfico
  const datasetIndices = [];
  let runningIndex = 0;
  selections.forEach((sel) => {
    for (let j = 0; j < (sel.numpyData?.length || 0); j++) {
      datasetIndices.push(runningIndex++);
    }
  });

  return (
    <>
      {/* Barra superior verde */}
      <div
        style={{
          width: '100%',
          background: '#219150',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          padding: '0 24px',
          height: 56,
          fontSize: 24,
          fontWeight: 600,
          boxSizing: 'border-box',
          letterSpacing: 1,
          zIndex: 100,
        }}
      >
        {/* Imagem da miniatura do site dentro de um círculo branco */}
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 40,
            width: 40,
            borderRadius: '50%',
            background: '#fff',
            marginRight: 10,
          }}
        >
          <img
            src="/favicon.ico"
            alt="Logo"
            style={{
              height: 28,
              width: 28,
              borderRadius: '0%',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </span>
        Plantspec Web
      </div>
      {/* Conteúdo principal */}
      <div
        ref={containerRef}
        style={{
          display: 'flex',
          width: '100%',
          height: 'calc(100vh - 56px)',
          userSelect: resizing ? 'none' : 'auto',
        }}
      >
        <div
          style={{
            width: `${leftWidth}%`,
            minWidth: 150,
            maxWidth: '80%',
            padding: 10,
            borderRight: '1px solid #ddd',
            boxSizing: 'border-box',
            overflow: 'auto',
          }}
        >
          <button onClick={addSelection}>Adicionar Seleção</button>
          {selections.map((sel, idx) => (
            <div
              key={idx}
              style={{
                marginBottom: 20,
                borderBottom: '1px solid #eee',
                overflow: 'auto',
                minHeight: 80,
                maxHeight: 400,
                paddingBottom: 10,
                background: '#fff',
                position: 'relative',
              }}
            >
              {/* Botão para remover grupo */}
              {selections.length > 1 && (
                <button
                  onClick={() => {
                    setSelections(prev => prev.filter((_, i) => i !== idx));
                  }}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: '#e74c3c',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '50%',
                    width: 28,
                    height: 28,
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: 18,
                    lineHeight: '28px',
                    textAlign: 'center',
                  }}
                  title="Remover grupo"
                >
                  ×
                </button>
              )}
              <h3 style={{ display: 'flex', alignItems: 'center' }}>
                Seleção {idx + 1}
                {/* Contador de espectros */}
                {sel.selectedDataset && (
                  <span
                    style={{
                      marginLeft: 10,
                      background: '#219150',
                      color: '#fff',
                      borderRadius: 12,
                      padding: '2px 10px',
                      fontSize: 14,
                      fontWeight: 500,
                      minWidth: 48,
                      textAlign: 'center',
                      display: 'inline-block',
                    }}
                    title="Quantidade de espectros selecionados / total disponíveis"
                  >
                    {`${sel.selectedData?.length || 0} / ${sel.filteredData?.length || 0}`}
                  </span>
                )}
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ minWidth: 60, fontWeight: 500, color: '#555' }}>Dataset:</span>
                <AutoComplete
                  field="name"
                  value={sel.selectedDataset}
                  suggestions={sel.filteredDataset ?? []}
                  completeMethod={(e) => searchDataset(e, idx)}
                  onChange={(e) =>
                    setSelections((prev) =>
                      prev.map((s, i) =>
                        i === idx
                          ? {
                              ...s,
                              selectedDataset: e.value,
                              filteredData: null,
                              selectedData: [],
                              numpyData: [],
                              loading: true,
                            }
                          : s
                      )
                    )
                  }
                  dropdown
                  placeholder="Selecione Dataset"
                />
                {/* Ícone de loading enquanto espectros estão sendo carregados */}
                {sel.loading && (
                  <span
                    style={{
                      display: 'inline-block',
                      marginLeft: 4,
                      width: 20,
                      height: 20,
                    }}
                    title="Carregando espectros..."
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 50 50"
                      style={{ display: 'block', animation: 'spin 1s linear infinite' }}
                    >
                      <circle
                        cx="25"
                        cy="25"
                        r="20"
                        fill="none"
                        stroke="#219150"
                        strokeWidth="5"
                        strokeDasharray="31.4 31.4"
                        strokeLinecap="round"
                      />
                    </svg>
                    <style>
                      {`
                        @keyframes spin {
                          100% { transform: rotate(360deg); }
                        }
                      `}
                    </style>
                  </span>
                )}
              </div>
              {sel.selectedDataset && sel.filteredData && !sel.loading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <span style={{ minWidth: 60, fontWeight: 500, color: '#555' }}>Espectros:</span>
                  <AutoComplete
                    field="name"
                    value={sel.selectedData}
                    suggestions={
                      sel.filteredData
                        ? sel.filteredData.filter(
                            (item) =>
                              !sel.selectedData.some(
                                (selected) => selected.value.data === item.value.data
                              )
                          )
                        : []
                    }
                    completeMethod={(e) => searchData(e, idx)}
                    onChange={(e) =>
                      setSelections((prev) =>
                        prev.map((s, i) =>
                          i === idx
                            ? { ...s, selectedData: e.value, numpyData: [] }
                            : s
                        )
                      )
                    }
                    multiple
                    dropdown
                    placeholder="Selecione Espectros"
                  />
                  {/* Botão Plotar Todos */}
                  <button
                    style={{
                      marginLeft: 4,
                      padding: '4px 10px',
                      background: '#219150',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontWeight: 500,
                      fontSize: 14,
                    }}
                    title="Selecionar todos os espectros"
                    onClick={() =>
                      setSelections((prev) =>
                        prev.map((s, i) =>
                          i === idx
                            ? {
                                ...s,
                                selectedData: sel.filteredData ? [...sel.filteredData] : [],
                                numpyData: [],
                              }
                            : s
                        )
                      )
                    }
                    disabled={!sel.filteredData || sel.filteredData.length === 0}
                  >
                    Plotar todos
                  </button>
                </div>
              )}
              {sel.loading && <p>Carregando dados...</p>}
            </div>
          ))}
        </div>
        {/* Linha divisória para redimensionar */}
        <div
          style={{
            width: 6,
            cursor: 'col-resize',
            background: resizing ? '#bbb' : '#eee',
            zIndex: 10,
          }}
          onMouseDown={startResizing}
        />
        <div
          style={{
            width: `${100 - leftWidth}%`,
            minWidth: 150,
            padding: 10,
            boxSizing: 'border-box',
            overflow: 'auto',
          }}
        >
          <h2>Graph</h2>
          {selections.some((s) => s.numpyData.length > 0) ? (
            <>
              <div
                ref={graphRef}
                style={{
                  position: 'relative',
                  width: graphWidth,
                  height: graphHeight,
                  minWidth: 300,
                  minHeight: 200,
                  background: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                  margin: '0 auto',
                  transition: 'box-shadow 0.2s',
                  boxShadow: resizingGraph ? '0 0 0 2px #21915055' : undefined,
                }}
              >
                <Line data={chartData} options={chartOptions} />
                {/* Handle de redimensionamento no canto inferior direito */}
                <div
                  style={{
                    position: 'absolute',
                    right: 0,
                    bottom: 0,
                    width: 18,
                    height: 18,
                    cursor: 'nwse-resize',
                    background: 'transparent',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'flex-end',
                  }}
                  onMouseDown={() => setResizingGraph(true)}
                  title="Redimensionar gráfico"
                >
                  <svg width="18" height="18">
                    <polyline points="4,18 18,18 18,4" fill="none" stroke="#219150" strokeWidth="2"/>
                  </svg>
                </div>
              </div>
              {/* Filtro de espectros */}
              <div style={{ marginTop: 24 }}>
                <input
                  type="text"
                  placeholder="Filtrar espectros..."
                  style={{
                    width: '100%',
                    maxWidth: 400,
                    marginBottom: 12,
                    padding: '6px 10px',
                    borderRadius: 4,
                    border: '1px solid #bbb',
                    fontSize: 15,
                  }}
                  value={filterSpectraText}
                  onChange={e => setFilterSpectraText(e.target.value)}
                />
                {chartData.datasets
                  .filter(ds =>
                    ds.label.toLowerCase().includes(filterSpectraText.toLowerCase())
                  )
                  .map((ds, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 8,
                      fontWeight: highlightedDataset === i ? 'bold' : 'normal',
                      opacity: hiddenDatasets.includes(ds._datasetIndex) ? 0.4 : 1,
                      cursor: 'pointer',
                      background: highlightedDataset === i ? '#f0f8ff' : 'transparent',
                      borderRadius: 6,
                      padding: '4px 8px',
                      transition: 'background 0.2s',
                    }}
                    onClick={() =>
                      setHighlightedDataset((prev) => (prev === ds._datasetIndex ? null : ds._datasetIndex))
                    }
                  >
                    {/* Ícone olho */}
                    <span
                      title={hiddenDatasets.includes(ds._datasetIndex) ? 'Mostrar espectro' : 'Ocultar espectro'}
                      style={{
                        marginRight: 8,
                        cursor: 'pointer',
                        width: 22,
                        height: 22,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        background: hiddenDatasets.includes(ds._datasetIndex) ? '#eee' : '#eafaf1',
                        border: '1px solid #bbb',
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        setHiddenDatasets(prev =>
                          prev.includes(ds._datasetIndex)
                            ? prev.filter(idx => idx !== ds._datasetIndex)
                            : [...prev, ds._datasetIndex]
                        );
                      }}
                    >
                      {/* SVG olho */}
                      <svg width="18" height="18" viewBox="0 0 20 20">
                        <ellipse
                          cx="10"
                          cy="10"
                          rx="7"
                          ry="4"
                          fill="none"
                          stroke="#219150"
                          strokeWidth="2"
                          opacity={hiddenDatasets.includes(ds._datasetIndex) ? 0.3 : 1}
                        />
                        <circle
                          cx="10"
                          cy="10"
                          r="1.8"
                          fill="#219150"
                          opacity={hiddenDatasets.includes(ds._datasetIndex) ? 0.2 : 1}
                        />
                      </svg>
                    </span>
                    {/* Amostra de cor */}
                    <span
                      style={{
                        display: 'inline-block',
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        background: ds.borderColor,
                        marginRight: 10,
                        border: '2px solid #ccc',
                      }}
                    />
                    {/* Nome do espectro */}
                    <span style={{ flex: 1, marginRight: 10, userSelect: 'text' }}>
                      {ds.label}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p>Selecione ao menos um espectro para visualizar o gráfico</p>
          )}
        </div>
      </div>
    </>
  );
};

// Adicione este bloco logo antes do componente App, se não quiser criar um arquivo CSS separado:
const customStyles = `
.p-autocomplete,
.p-autocomplete-panel,
.p-autocomplete .p-inputtext {
  background: #fff !important;
  opacity: 1 !important;
}
.p-autocomplete-multiple-container {
  flex-direction: column !important;
  align-items: flex-start !important;
  gap: 2px;
}
.p-autocomplete-token {
  margin: 0 !important;
  width: 100%;
}
`;
if (typeof document !== "undefined" && !document.getElementById('custom-autocomplete-styles')) {
  const style = document.createElement('style');
  style.id = 'custom-autocomplete-styles';
  style.innerHTML = customStyles;
  document.head.appendChild(style);
}

export default App;